import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_LOCALE,
  LocaleContext,
  getDictionary,
  type Locale,
  type LocaleContextValue,
} from './index';

interface LocaleProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
}

/**
 * Holds the active locale and keeps <html lang> and the document title in sync
 * with it. Locale lives in state rather than a URL segment or storage: there is
 * only one locale today, and routing by locale is a decision to make when the
 * second one actually lands.
 */
export function LocaleProvider({ children, initialLocale = DEFAULT_LOCALE }: LocaleProviderProps) {
  const [locale, setLocale] = useState<Locale>(initialLocale);

  const t = useMemo(() => getDictionary(locale), [locale]);

  useEffect(() => {
    document.documentElement.lang = t.meta.htmlLang;
    document.title = t.meta.documentTitle;
    // The static tag in index.html is what crawlers read; this keeps the live
    // DOM honest if a second locale ever lands.
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute('content', t.meta.metaDescription);
  }, [t]);

  const handleSetLocale = useCallback((next: Locale) => setLocale(next), []);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, t, setLocale: handleSetLocale }),
    [locale, t, handleSetLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
